import axios from 'axios';
import { ReferralFilterParams } from '../models/referralModel';

const loadAbort = () => {
  return new AbortController();
};

/**
 * Obtiene la lista general de referidos con paginación, filtros y KPIs
 */
export const getAdminReferralListEndpoint = (
  filters: ReferralFilterParams,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/user/v1/admin/referrals`,
        params: filters,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Obtiene la lista de responsables disponibles
 */
export const getResponsiblesListEndpoint = (token: string, countryCode?: string) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/user/v1/admin/referrals/responsibles`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(countryCode ? { 'x-api-country': countryCode } : {}),
        },
      }),
    controller,
  };
};

/**
 * Obtiene el detalle de un referidor y sus prospectos
 */
export const getAdminReferralByReferrerEndpoint = (
  referrerProfileId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/user/v1/admin/referrals/by-referrer/${referrerProfileId}`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Actualiza el estado de un referido
 */
export const updateReferralStatusEndpoint = (
  referralId: string,
  status: string,
  token: string,
  extraData?: { deal_amount?: number; unit_id?: string },
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'patch',
        url: `${urlService}/user/v1/admin/referrals/${referralId}/status`,
        data: { status, ...extraData },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Obtiene la unidad asignada a un usuario desde pre-sales-service
 */
export const getAssignedUnitByUserEndpoint = (
  userId: string,
  referralDate: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/pre-sales/v1/admin/model-units/by-user/${userId}`,
        params: { referral_date: referralDate },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Obtiene el contrato de renta pagado de un inquilino desde transaction-service
 */
export const getRentalContractByTenantEndpoint = (
  tenantId: string,
  referralDate: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/ts/v1/admin/contracts/by-tenant/${tenantId}`,
        params: { referral_date: referralDate },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Obtiene la propiedad publicada de un propietario desde rs-service
 */
export const getPublishedPropertyByLandlordEndpoint = (
  landlordId: string,
  referralDate: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/rs/v1/admin/properties/by-landlord/${landlordId}`,
        params: { referral_date: referralDate },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Obtiene la lista del Dashboard de Pagos
 */
export const getAdminPaymentListEndpoint = (
  filters: Record<string, any>,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'get',
        url: `${urlService}/user/v1/admin/referrals/payments`,
        params: filters,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Envía recordatorio al referidor
 */
export const sendReferralReminderEndpoint = (
  referralId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: `${urlService}/user/v1/admin/referrals/${referralId}/reminder`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Marca un pago como liquidado
 */
export const markReferralPaidEndpoint = (
  referralId: string,
  data: { payment_reference?: string },
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'patch',
        url: `${urlService}/user/v1/admin/referrals/${referralId}/mark-paid`,
        data,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Registra al prospecto referido como cliente y vincula su perfil
 */
export const registerReferralProspectEndpoint = (
  referralId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'post',
        url: `${urlService}/user/v1/admin/referrals/${referralId}/register-prospect`,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

/**
 * Actualiza el responsable asignado a un referido
 */
export const updateReferralResponsibleEndpoint = (
  referralId: string,
  assignedResponsibleId: string,
  token: string,
) => {
  const controller = loadAbort();
  return {
    call: (urlService: string) =>
      axios({
        method: 'patch',
        url: `${urlService}/user/v1/admin/referrals/${referralId}/responsible`,
        data: { assigned_responsible_id: assignedResponsibleId },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      }),
    controller,
  };
};

